import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider.js';

/**
 * Unauthenticated entry page. Stands alone — not wrapped by Layout. OneView
 * styling: calm enterprise card centred on the app surface, a low-opacity
 * tiled hex motif behind it for brand presence without flash. Auth flow is
 * unchanged from the previous incarnation; only the chrome moved.
 */

const DEV_PERSONAS = [
  {
    token: 'dev-admin-token',
    label: 'Andre Witte',
    role: 'OneNebula Admin',
    tenant: 'onenebula',
  },
  {
    token: 'dev-medewerker-token',
    label: 'Dev Employee',
    role: 'OneNebula Employee',
    tenant: 'onenebula',
  },
  {
    token: 'dev-exco-token',
    label: 'Dev Exco',
    role: 'OneNebula Exco',
    tenant: 'onenebula',
  },
  {
    token: 'dev-sensitive-token',
    label: 'Dev Sensitive',
    role: 'OneNebula Sensitive',
    tenant: 'onenebula',
  },
];

const ERROR_MESSAGES: Record<string, string> = {
  missing_params: 'Sign-in failed. Please try again.',
  missing_state: 'Your sign-in session has expired. Please try again.',
  state_mismatch: 'Security error during sign-in. Please try again.',
  token_exchange: 'Microsoft did not accept the sign-in. Please try again.',
  id_token_invalid: 'Microsoft token could not be verified.',
  no_app_account:
    'You do not yet have access to this Knowledgebase. Ask your IT administrator to add you to the right group.',
  provision_failed: 'Account creation failed. Please try again or contact an administrator.',
  deactivated: 'Your account has been deactivated. Please contact an administrator.',
};

export function LoginPage(): JSX.Element {
  const { setDevToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const errorKey = new URLSearchParams(location.search).get('error');
  const errorMsg = errorKey ? (ERROR_MESSAGES[errorKey] ?? `Unknown error: ${errorKey}`) : null;

  const devTokensEnabled = import.meta.env.DEV;

  function pickDev(token: string) {
    setDevToken(token);
    navigate('/', { replace: true });
  }

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center bg-nebula-app p-6">
      {/* Decorative hex motif. Tiled pointy-top hexagons at ~28px outer
          radius, very low opacity. Pure decoration — aria-hidden. */}
      <HexMotif />

      <div className="relative z-10 w-full max-w-md rounded-md border border-nebula-border-soft bg-nebula-surface p-8 shadow-card">
        <header className="mb-6 flex flex-col items-center text-center">
          <img src="/1Nebula-01.png" alt="1Nebula" className="h-10 w-auto" />
          <h1 className="ov-h-section mt-4">Sign in to the Knowledgebase</h1>
          <p className="ov-body mt-1 text-nebula-fg-3">Use your OneNebula corporate account.</p>
        </header>

        {errorMsg && (
          <p className="mb-4 rounded-md border border-nebula-error/30 bg-nebula-error-bg px-3 py-2 text-sm text-nebula-error">
            {errorMsg}
          </p>
        )}

        {/* Plain anchor — not a fetch — because the backend issues a 302 to
            login.microsoftonline.com which a fetch can't follow cross-origin.
            Full page navigation is the right primitive here. */}
        <a
          href="/api/v1/oauth/entra/login"
          className="flex w-full items-center justify-center gap-3 rounded-md bg-nebula-primary px-4 py-3 font-ui text-white transition hover:bg-nebula-primary/90"
        >
          <MicrosoftLogo />
          <span>Sign in with Microsoft</span>
        </a>

        <p className="ov-meta mt-3 text-center text-nebula-fg-3">
          Need access? Contact your tenant admin.
        </p>

        {devTokensEnabled && (
          <>
            <div className="my-8 flex items-center gap-3 text-[11px] uppercase tracking-wider text-nebula-fg-4">
              <span className="h-px flex-1 bg-nebula-border-soft" />
              <span>Dev personas (local only)</span>
              <span className="h-px flex-1 bg-nebula-border-soft" />
            </div>
            <div className="space-y-2">
              {DEV_PERSONAS.map((p) => (
                <button
                  key={p.token}
                  type="button"
                  onClick={() => pickDev(p.token)}
                  className="flex w-full items-center justify-between rounded-md border border-nebula-border-soft bg-white px-4 py-3 text-left transition hover:border-nebula-secondary hover:bg-nebula-tint"
                >
                  <span>
                    <span className="block font-ui text-sm text-nebula-fg-1">{p.label}</span>
                    <span className="ov-meta block text-nebula-fg-3">
                      {p.role} · tenant: {p.tenant}
                    </span>
                  </span>
                  <span className="text-nebula-fg-4">→</span>
                </button>
              ))}
            </div>
            <p className="ov-meta mt-6 text-nebula-fg-4">
              Dev tokens only work in{' '}
              <code className="rounded bg-nebula-gray-5 px-1 font-mono text-nebula-fg-2">
                npm run dev
              </code>{' '}
              and are disabled in production builds.
            </p>
          </>
        )}
      </div>

      <p className="ov-meta-upper relative z-10 mt-auto pb-6 pt-10 text-center text-nebula-fg-4">
        OneNebula · Knowledgebase
      </p>
    </div>
  );
}

function HexMotif(): JSX.Element {
  // Pointy-top hexagon, outer radius r = 28. Vertices at angles
  // 30/90/150/210/270/330. Tile rect of 2*sqrt(3)*r ≈ 96.99 wide and 6r/2 = 84
  // tall would interlock; we use a simple offset pattern via two stacked
  // polygons in a 97x84 tile. The exact math isn't load-bearing — the
  // pattern reads as "hex grid" at 4% opacity.
  return (
    <svg
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 h-full w-full text-nebula-primary"
      width="100%"
      height="100%"
    >
      <defs>
        <pattern id="ov-hex-motif" x="0" y="0" width="97" height="84" patternUnits="userSpaceOnUse">
          {/* Hex 1 — origin */}
          <polygon
            points="28,0 56,16.165 56,48.495 28,64.66 0,48.495 0,16.165"
            stroke="currentColor"
            strokeWidth="1"
            fill="none"
          />
          {/* Hex 2 — offset to interlock */}
          <polygon
            points="76.5,42 104.5,58.165 104.5,90.495 76.5,106.66 48.5,90.495 48.5,58.165"
            stroke="currentColor"
            strokeWidth="1"
            fill="none"
          />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#ov-hex-motif)" opacity="0.04" />
    </svg>
  );
}

function MicrosoftLogo(): JSX.Element {
  return (
    <svg width="20" height="20" viewBox="0 0 23 23" aria-hidden="true">
      <rect x="1" y="1" width="10" height="10" fill="#F25022" />
      <rect x="12" y="1" width="10" height="10" fill="#7FBA00" />
      <rect x="1" y="12" width="10" height="10" fill="#00A4EF" />
      <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
    </svg>
  );
}
